import { Link } from 'react-router-dom';
import type { Property } from '../../../lib/supabase';
import LazyImg from '../../../components/base/LazyImg';
import { PropertyCardSkeleton } from '../../../components/base/Skeleton';
import { trimToDistrict } from '../../../lib/locationUtils';

interface LatestPropertiesSectionProps {
  properties: Property[];
  loading: boolean;
  onPublish?: () => void;
}

const PLACEHOLDER_IMAGES = [
  'https://public.readdy.ai/ai/img_res/04d86ac4e8b2bc4bebf646b2da0faef6.jpg',
  'https://readdy.ai/api/search-image?query=cozy%20living%20room%20apartment%20warm%20lighting%20comfortable%20furniture%20clean%20simple%20interior%20professional%20real%20estate%20photo%20neutral%20background&width=600&height=400&seq=lp2&orientation=landscape',
  'https://readdy.ai/api/search-image?query=contemporary%20apartment%20bedroom%20natural%20light%20clean%20design%20simple%20furniture%20white%20walls%20professional%20photography&width=600&height=400&seq=lp3&orientation=landscape',
  'https://readdy.ai/api/search-image?query=spacious%20modern%20dining%20room%20apartment%20clean%20minimal%20design%20warm%20lighting%20neutral%20colors%20professional%20interior%20photography&width=600&height=400&seq=lp4&orientation=landscape',
];

function getPlaceholder(id: string) {
  return PLACEHOLDER_IMAGES[id.charCodeAt(0) % PLACEHOLDER_IMAGES.length];
}

export default function LatestPropertiesSection({ properties, loading, onPublish }: LatestPropertiesSectionProps) {
  return (
    <section className="py-14 md:py-20 bg-white border-t border-stone-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* 標題 */}
        <div className="flex items-end justify-between mb-8 md:mb-10">
          <div>
            <p className="text-xs text-teal-600 uppercase tracking-wider mb-1.5">即時上架</p>
            <h2 className="text-2xl md:text-4xl font-black text-gray-900">最新房源</h2>
          </div>
          <Link
            to="/regions"
            className="flex items-center gap-1.5 text-sm text-teal-600 hover:text-teal-700 font-semibold transition-colors cursor-pointer whitespace-nowrap"
          >
            查看全部 <i className="ri-arrow-right-line text-sm"></i>
          </Link>
        </div>

        {/* 骨架屏（載入中） */}
        {loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <PropertyCardSkeleton key={i} />
            ))}
          </div>
        )}

        {/* 有房源 */}
        {!loading && properties.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {properties.slice(0, 6).map(property => {
              const img = property.photos?.[0] ?? getPlaceholder(property.id);
              const pos = property.photo_positions?.[0] ?? '50% 50%';
              const zoom = property.photo_zooms?.[0] ?? 1;
              return (
                <Link
                  key={property.id}
                  to={`/property/${property.id}`}
                  className="group bg-white rounded-2xl overflow-hidden border border-stone-100 hover:border-teal-200 hover:-translate-y-1 transition-all duration-200 cursor-pointer"
                >
                  {/* 圖片：LazyImg 懶載入 */}
                  <div className="relative h-48 overflow-hidden bg-gray-100">
                    <LazyImg
                      src={img}
                      alt={property.title}
                      containerClassName="absolute inset-0"
                      imgClassName="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      imgStyle={{ objectPosition: pos, transform: `scale(${zoom})`, transformOrigin: pos }}
                    />
                    <span className="absolute top-3 left-3 px-2.5 py-1 bg-teal-500 text-white text-xs font-semibold rounded-full whitespace-nowrap">
                      零仲介費
                    </span>
                    <span className="absolute top-3 right-3 flex items-center gap-1 px-2.5 py-1 bg-white/90 text-teal-600 text-xs font-bold rounded-full border border-teal-100 whitespace-nowrap">
                      <i className="ri-shield-check-line text-xs"></i>零仲介費
                    </span>
                    {property.pet_allowed === '可養寵物' && (
                      <span className="absolute bottom-3 left-3 flex items-center gap-1 px-2 py-0.5 bg-amber-400/90 text-white text-xs rounded-full whitespace-nowrap">
                        <i className="ri-bear-smile-line text-xs"></i>可養寵物
                      </span>
                    )}
                  </div>

                  {/* 內容 */}
                  <div className="p-4 md:p-5">
                    <h3 className="font-bold text-gray-900 text-sm leading-snug line-clamp-1 mb-1.5 group-hover:text-teal-700 transition-colors">
                      {property.title}
                    </h3>
                    <p className="text-xs text-gray-400 flex items-center gap-1 mb-3 truncate">
                      <i className="ri-map-pin-line text-teal-400 flex-shrink-0"></i>
                      <span className="truncate">{trimToDistrict(property.location)}</span>
                    </p>
                    <div className="flex items-center gap-2 text-xs text-gray-400 mb-3">
                      <span className="flex items-center gap-0.5 bg-gray-50 px-2 py-0.5 rounded-md">
                        <i className="ri-ruler-line text-gray-300"></i>{property.area}坪
                      </span>
                      <span className="flex items-center gap-0.5 bg-gray-50 px-2 py-0.5 rounded-md">
                        {['店面', '廠房', '辦公室'].includes(property.property_type || '')
                          ? <><i className="ri-store-2-line text-gray-300"></i>{property.property_type}・{property.area}坪・{property.bathrooms}衛</>
                          : <><i className="ri-door-line text-gray-300"></i>{property.rooms}房{property.bathrooms}衛</>
                        }
                      </span>
                      {property.water_bill === '含水費' && (
                        <span className="flex items-center gap-0.5 bg-teal-50 text-teal-600 px-2 py-0.5 rounded-md">
                          <i className="ri-drop-line text-xs"></i>含水費
                        </span>
                      )}
                    </div>
                    <div className="flex items-end justify-between">
                      <div>
                        <span className="text-xl font-black text-red-500">NT$ {property.price.toLocaleString()}</span>
                        <span className="text-xs text-gray-400">/月</span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-300">
                        <i className="ri-eye-line"></i>
                        <span>{property.view_count || 0}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {/* 無房源 */}
        {!loading && properties.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 bg-stone-50 rounded-2xl">
            <div className="w-16 h-16 flex items-center justify-center bg-teal-50 rounded-full mb-4">
              <i className="ri-home-4-line text-3xl text-teal-400"></i>
            </div>
            <p className="font-semibold text-gray-600 mb-2">目前尚無上架房源</p>
            <p className="text-sm text-gray-400 mb-6">成為第一個發布零仲介費房源的屋主！</p>
            {onPublish ? (
              <button
                type="button"
                onClick={onPublish}
                className="px-6 py-2.5 bg-teal-500 text-white rounded-full font-semibold text-sm hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                立即發布房源
              </button>
            ) : (
              <Link
                to="/publish"
                className="px-6 py-2.5 bg-teal-500 text-white rounded-full font-semibold text-sm hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                立即發布房源
              </Link>
            )}
          </div>
        )}

        {/* 查看更多按鈕 */}
        {!loading && properties.length > 0 && (
          <div className="mt-8 md:mt-10 text-center">
            <Link
              to="/regions"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-teal-500 hover:bg-teal-600 text-white rounded-full font-bold text-sm transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-search-line"></i>
              搜尋更多台灣六都房源
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
